import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

interface RevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "section" | "article" | "header" | "footer" | "li" | "ul" | "ol";
}

export function Reveal({ children, className, delay = 0, as: Tag = "div" }: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    // threshold 0 (not a ratio): tall wrappers can never expose 12% of their
    // box inside the viewport, which used to leave whole sections stuck at
    // opacity 0. A timeout fail-safe also guarantees content becomes visible
    // even if the observer never fires (bfcache restore, scroll anchoring).
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting || e.intersectionRatio > 0) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0, rootMargin: "200px 0px 200px 0px" },
    );
    io.observe(el);
    const failSafe = window.setTimeout(() => {
      setVisible(true);
      io.disconnect();
    }, 2000);
    return () => {
      window.clearTimeout(failSafe);
      io.disconnect();
    };
  }, [reduced]);

  const Component = Tag as unknown as "div";
  return (
    <Component
      ref={ref as unknown as React.RefObject<HTMLDivElement>}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        "transition-all duration-700 ease-out will-change-transform",
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
        className,
      )}
    >
      {children}
    </Component>
  );
}
